import React from 'react';
import { Software, License, LicensedSoftware } from '~/lib/ApiModel';
import { FormGroup, Label, Input, FormFeedback } from 'reactstrap';
import ErrorList from '../ErrorList';
import { hasItem, range } from '~/lib/helper/ArrayHelper';
import { softwareIsBinary, SoftwareGroup, softwareGroups } from '~/lib/helper/SoftwareHelper';
import { licenseCountForSelectedSoftware, selectedSoftwareForSoftwareGroup } from '~/lib/helper/LicenseHelper';
import { asNumberOrDefault, asNumberOrThrow, formatCurrency } from '~/lib/helper/StringHelper';
import { getAppContext } from '~/lib/AppContext';

type LicenseGroupSelectProps = {
    group: SoftwareGroup;
    onChange: (selection: number) => unknown;
    selected?: number;
    errors?: string[];
};

type IndividualLicenseOptionProps = {
    software: Software;
    onChange: (count: number) => unknown;
    count: number;
    errors?: string[];
};

class BinaryLicenseOption extends React.Component<IndividualLicenseOptionProps> {
    render() {
        const { software, onChange = () => {}, count, errors } = this.props;
        return (
            <FormGroup check>
                <Label check>
                    <Input
                        invalid={errors && errors.length > 0}
                        type="checkbox"
                        checked={count > 0}
                        onChange={(e) => onChange(e.target.checked ? software.maximum_licence_count : 0)}
                    />
                    {software.name} (+
                    {formatCurrency(software.cost_per_licence_per_month * software.maximum_licence_count)} per month)
                </Label>
                <FormFeedback>
                    <ErrorList errors={errors} />
                </FormFeedback>
            </FormGroup>
        );
    }
}

class LicenseCountSelect extends React.Component<IndividualLicenseOptionProps> {
    render() {
        const { software, onChange = () => {}, count, errors } = this.props;
        const id = `softwareOption${software.id}`;
        return (
            <FormGroup>
                <Label for={id}>{software.name}</Label>
                <Input
                    type="select"
                    id={id}
                    value={count}
                    invalid={errors && errors.length > 0}
                    onChange={(e) => onChange(asNumberOrDefault(e.target.value, 0))}
                >
                    <option value={0}>Not Required</option>
                    {range(
                        software.minimum_licence_count,
                        software.maximum_licence_count,
                        software.licence_step_count
                    ).map((c) => (
                        <option key={c} value={c}>
                            {c} license{c > 1 && 's'} (+${(software.cost_per_licence_per_month * c).toFixed(2)} per
                            month)
                        </option>
                    ))}
                </Input>
                <FormFeedback>
                    <ErrorList errors={errors} />
                </FormFeedback>
            </FormGroup>
        );
    }
}

class LicenseGroupSelect extends React.Component<LicenseGroupSelectProps> {
    render() {
        const { group, selected, onChange = () => {}, errors } = this.props;
        const id = 'softwareOptionGroup' + group.name;
        return (
            <FormGroup>
                <Label for={id}>{group.name}</Label>
                <Input
                    type="select"
                    id={id}
                    invalid={errors && errors.length > 0}
                    value={selected ?? ''}
                    onChange={(e) => onChange(asNumberOrThrow(e.target.value))}
                >
                    {group.software.map((s) => (
                        <option key={s.id} value={s.id}>
                            {s.name}{' '}
                            {s.cost_per_licence_per_month > 0 && `(+$${s.cost_per_licence_per_month.toFixed(2)})`}
                        </option>
                    ))}
                </Input>
                <FormFeedback>
                    <ErrorList errors={errors} />
                </FormFeedback>
            </FormGroup>
        );
    }
}

type LicenseOptionsProps = {
    software: Software[];
    licenses: License[];
    existingLicenses?: LicensedSoftware[];
    errors: { [softwareId: string]: string[] };
    onLicensesChanged: (licenses: License[]) => unknown;
};

export default class LicenseOptions extends React.Component<LicenseOptionsProps> {
    updateLicenseCount(software: Software, count: number) {
        const { licenses, onLicensesChanged } = this.props;
        const newLicenses = licenses
            .filter((l) => l.software_id !== software.id)
            .concat([
                {
                    software_id: software.id,
                    count: count,
                },
            ]);
        onLicensesChanged(newLicenses);
    }

    updateGroupLicense(group: SoftwareGroup, softwareId: number) {
        const { licenses, onLicensesChanged } = this.props;
        const newLicenses = licenses
            .filter((l) => !group.software.find((s) => s.id === l.software_id)) //remove all software from group and re-add
            .concat([
                {
                    software_id: softwareId,
                    count: 1,
                },
            ]);
        onLicensesChanged(newLicenses);
    }

    errorsForGroup(group: SoftwareGroup) {
        const allErrors = this.props.errors;
        let groupErrors: string[] = [];
        for (const software of group.software) {
            const allErrs = allErrors[software.id];
            if (allErrs) {
                groupErrors = groupErrors.concat(allErrs);
            }
        }
        return groupErrors;
    }

    renderSoftwareGroups() {
        const { licenses, errors, software } = this.props;
        if (this.props.software.length === 0) return null;
        const groups = softwareGroups(software);

        return groups.map((g) => {
            if (hasItem(g.software) && g.software.length === 1) {
                const software = g.software[0];
                const count = licenseCountForSelectedSoftware(licenses, software.id);
                if (softwareIsBinary(software)) {
                    return (
                        <BinaryLicenseOption
                            key={g.name}
                            software={software}
                            count={count}
                            errors={errors[software.id]}
                            onChange={(count) => this.updateLicenseCount(software, count)}
                        />
                    );
                } else {
                    return (
                        <LicenseCountSelect
                            key={g.name}
                            software={software}
                            count={count}
                            errors={errors[software.id]}
                            onChange={(count) => this.updateLicenseCount(software, count)}
                        />
                    );
                }
            } else {
                const existingSelection = selectedSoftwareForSoftwareGroup(licenses, software, g.name);
                return (
                    <LicenseGroupSelect
                        key={g.name}
                        errors={this.errorsForGroup(g)}
                        selected={existingSelection?.id}
                        group={g}
                        onChange={(softwareId) => this.updateGroupLicense(g, softwareId)}
                    />
                );
            }
        });
    }

    renderInvalidLicenses() {
        const { existingLicenses } = this.props;
        if (!existingLicenses) return null;

        const invalid_software = existingLicenses.filter((existing) => existing.incompatible);

        if (invalid_software.length === 0) return null;

        return (
            <>
                <p>
                    <b>
                        These licenses are incompatible with the selected operating system and will be cancelled if you
                        proceed
                    </b>
                </p>
                {invalid_software.map((s) => {
                    const id = 'invalidSoftware' + s.software.name;
                    return (
                        <FormGroup key={s.software.id}>
                            <Label for={id}>{s.software.group ? s.software.group : s.software.name}</Label>
                            <Input
                                type="text"
                                disabled
                                id={id}
                                value={s.software.group ? s.software.name : s.licence_count}
                            ></Input>
                        </FormGroup>
                    );
                })}
            </>
        );
    }

    render() {
        return (
            <>
                {this.props.software.length > 0 && (
                    <p>
                        {getAppContext().config.brandName} can supply licenses for compatible third-party software as
                        part of your recurring invoice.
                    </p>
                )}
                {this.renderSoftwareGroups()}
                {this.renderInvalidLicenses()}
            </>
        );
    }
}
